import React, { useEffect, useState } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import { Button, Form, Card, Alert, Row, Col } from 'react-bootstrap';
import { createNavInstance } from '../../../Models/Nav';
import { toast, ToastContainer } from 'react-toastify';
import { SketchPicker } from 'react-color';
import MediaSelector from '../Media/MediaSelector';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faImage, faGlobe, faCheck, faSearch, faCog } from '@fortawesome/free-solid-svg-icons';
import { LanguageService } from '../../../Services/Axios/Api';
import LanguageSelector from '../../../Components/LanguageSelector/LanguageSelector';
import { availableAnimations } from '../../../Services/Animations/availableAnimations';

const NavBuilder = () => {
    const [logo, setLogo] = useState(null);
    const [navInstance, setNavInstance] = useState(createNavInstance());
    const [menuItems, setMenuItems] = useState(navInstance.getMenuItems());
    const [newMenuItem, setNewMenuItem] = useState("");
    const [newMenuLink, setNewMenuLink] = useState("");
    const [newItemType, setNewItemType] = useState("link"); 
    const [newButtonIcon, setNewButtonIcon] = useState(null);
    const [editingItem, setEditingItem] = useState(null);
    const [editedText, setEditedText] = useState("");
    const [editedLink, setEditedLink] = useState("");
    const [editedOrder, setEditedOrder] = useState("");
    
    // Style states
    const [showBgColorPicker, setShowBgColorPicker] = useState(false);
    const [showTextColorPicker, setShowTextColorPicker] = useState(false);
    const [showHoverColorPicker, setShowHoverColorPicker] = useState(false);
    const [currentStyle, setCurrentStyle] = useState(navInstance.getStyle());
    const [currentAnimation, setCurrentAnimation] = useState(navInstance.getAnimation());
    const [navSettings, setNavSettings] = useState(navInstance.getSettings());
    const [showMediaSelector, setShowMediaSelector] = useState(false);
    const [currentLanguage, setCurrentLanguage] = useState('');
    const [availableLanguages, setAvailableLanguages] = useState([]);   
    const [languageAlertType, setLanguageAlertType] = useState('info');
    const [showLanguageAlert, setShowLanguageAlert] = useState(false);
    const [isLanguagesLoaded, setIsLanguagesLoaded] = useState(false);

    useEffect(() => {
        loadLanguages();
    }, []);

    // Load data after languages are loaded and current language is set
    useEffect(() => {
        if (isLanguagesLoaded && currentLanguage) {
            getData();
        }
    }, [isLanguagesLoaded, currentLanguage]);

    const getData = async () => {
        try {
            console.log('NavBuilder: Loading data for language:', currentLanguage);
            await navInstance.load(currentLanguage);
            setMenuItems([...navInstance.getMenuItems()]);
            setCurrentStyle(navInstance.getStyle());
            setCurrentAnimation(navInstance.getAnimation());
            setNavSettings(navInstance.getSettings());
            const navLogo = navInstance.getLogo();
            console.log('Current logo:', navLogo); // Debug log
            if (navLogo) {
                setLogo(navLogo);
            }
        } catch (error) {
            console.error('Error loading navigation data:', error);
            toast.error('Failed to load navigation data');
        }
    };
    const loadLanguages = async () => {
        try {
            const response = await LanguageService.getLanguages();
            setAvailableLanguages(response.result);
            
            // Set default language from API
            const defaultLanguage = response.result.find(lang => lang.isDefault)?.code || 'en';
            setCurrentLanguage(defaultLanguage);
            setIsLanguagesLoaded(true);
            
            console.log('NavBuilder: Language loaded:', defaultLanguage);
        } catch (error) {
            console.error('Error loading languages:', error);
            toast.error('Failed to load languages');
            setCurrentLanguage('en');
            setIsLanguagesLoaded(true);
        }
    }

    const handleLogoSelect = (selectedPath) => {
        if (selectedPath) {
            setLogo(selectedPath);
            navInstance.setLogo(selectedPath);
        }
    };

    const addMenuItem = () => {
        if (newMenuItem.trim() === "" || newMenuLink.trim() === "") return;

        const newItem = {
            id: Date.now(),
            type: newItemType,
            order: menuItems.length + 1,
            ...(newItemType === "link"
                ? { text: newMenuItem, link: newMenuLink }
                : { 
                    button: { 
                        id: Date.now(), 
                        text: newMenuItem, 
                        link: newMenuLink,
                        type: 'link',
                        className: 'NavBtn',
                        style: {},
                        icon: newButtonIcon || null
                    } 
                }
            )
        };

        navInstance.addMenuItem(newItem);
        setMenuItems([...navInstance.getMenuItems()]);
        setNewMenuItem("");
        setNewMenuLink("");
        setNewItemType("link");
        setNewButtonIcon(null);
    };

    const handleRemoveItem = (id) => {
        navInstance.removeMenuItem(id);
        setMenuItems([...navInstance.getMenuItems()]);
    };

    const startEditing = (item) => {
        if (item.type === 'link') {
            setEditingItem(item.id);
            setEditedText(item.text);
            setEditedLink(item.link);
            setEditedOrder(item.order || '');
        } else {
            setEditingItem(item.button.id);
            setEditedText(item.button.text);
            setEditedLink(item.button.link);
            setEditedOrder(item.button.order || '');
        }
    };

    const saveEdit = (id, type = 'link') => {
        const item = menuItems.find(mi => mi.id === id);
        navInstance.updateMenuItem(id, { 
            text: editedText, 
            link: editedLink, 
            order: editedOrder,
            ...(item?.type === 'button' ? { icon: item?.button?.icon ?? null } : {})
        }, type);
        setMenuItems([...navInstance.getMenuItems()]);
        setEditingItem(null);
    };

    // Style handlers
    const handleBgColorChange = (color) => {
        const updatedStyle = {
            ...currentStyle,
            NavBasic: {
                ...currentStyle.NavBasic,
                backgroundColor: color.hex
            }
        };
        setCurrentStyle(updatedStyle);
        navInstance.setStyle(updatedStyle);
    };

    const handleTextColorChange = (color) => {
        const updatedStyle = {
            ...currentStyle,
            NavBasicLinkItem: {
                ...currentStyle.NavBasicLinkItem,
                color: color.hex
            }
        };
        setCurrentStyle(updatedStyle);
        navInstance.setStyle(updatedStyle);
    };

    const handleHoverColorChange = (color) => {
        const updatedStyle = {
            ...currentStyle,
            NavBasicLinkItemHover: {
                ...currentStyle.NavBasicLinkItemHover,
                color: color.hex
            }
        };
        setCurrentStyle(updatedStyle);
        navInstance.setStyle(updatedStyle);
    };

    const handlePositionChange = (e) => {
        const position = e.target.value;
        const updatedStyle = {
            ...currentStyle,
            NavBasic: {
                ...currentStyle.NavBasic,
                position: position
            }
        };
        setCurrentStyle(updatedStyle);
        navInstance.setStyle(updatedStyle);
    };

    const handleNavTypeChange = (e) => {
        navInstance.setType(e.target.value);
    };

    // Animation handlers
    const handleAnimationTypeChange = (e) => {
        const value = e.target.value;
        const updated = { ...currentAnimation, animationType: value };
        setCurrentAnimation(updated);
        navInstance.setAnimation(updated);
    };
    const handleAnimationDurationChange = (e) => {
        const value = e.target.value;
        const updated = { ...currentAnimation, animationDuration: value };
        setCurrentAnimation(updated);
        navInstance.setAnimation(updated);
    };
    const handleAnimationDelayChange = (e) => {
        const value = e.target.value;
        const updated = { ...currentAnimation, animationDelay: value };
        setCurrentAnimation(updated);
        navInstance.setAnimation(updated);
    };

    const handleSettingsChange = (key, value) => {
        const updated = { ...navSettings, [key]: value };
        setNavSettings(updated);
        navInstance.setSettings(updated);
    };

    const handleSaveChanges = async () => {
        try {
            await navInstance.save();
            toast.success("Changes saved successfully!");
            getData();
        } catch (error) {
            toast.error("Failed to save changes. Please try again.");
        }
    };
    const handleLanguageChange = async (newLanguage) => {
        setCurrentLanguage(newLanguage);
        setShowLanguageAlert(true);
        setLanguageAlertType('info');
        toast.success(`Switched to ${availableLanguages.find(l => l.code === newLanguage)?.name || newLanguage.toUpperCase()}`);
        setTimeout(() => setShowLanguageAlert(false), 3000);
        // getData will be called automatically by the useEffect when currentLanguage changes
    };

    // const handleCopyFromLanguage = async (languageCode) => {
        
    //     try {
    //         // const response = await PageService.CopyPageFromLanguage(slug, languageCode, currentLanguage );
    //         // toast.success(response.result);
    //         setTimeout(() => {
    //             // navigate(`/bj-admin/pages/page-builder/${id}?slug=${slug}&lang=${currentLanguage}`);
    //             getData();
    //         }, 1000);
    //     } catch (error) {
    //         console.error('Error copying from language:', error);
    //         toast.error('Failed to copy from language');
    //     }
    // };

    return (
        <div className="container mt-4">
            <h3 className="mb-3">Navbar Builder</h3>
            <Card className="mb-4 language-management-card">
                        <Card.Header className="language-management-header">
                            <Row     className="">
                                <Col md={6}>
                                    <div className="d-flex align-items-center gap-3">
                                        <FontAwesomeIcon icon={faGlobe} className="text-primary" />
                                        <div>
                                            <h6 className="mb-0">Language Management</h6>
                                            <small className="text-muted">
                                                Managing content for: <strong>{availableLanguages.find(l => l.code === currentLanguage)?.name}</strong>
                                            </small>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={6} className="d-flex justify-content-end">
                                    <LanguageSelector
                                        currentLanguage={currentLanguage}
                                        availableLanguages={availableLanguages}
                                        onLanguageChange={handleLanguageChange}
                                        showBadge={false}
                                        showCopyFromLanguage={false}
                                        // onCopyFromLanguage={handleCopyFromLanguage}
                                    />
                                </Col>
                            </Row>
                        </Card.Header>
                        
                        {/* Language Alert */}
                        {showLanguageAlert && (
                            <Card.Body className="p-3 language-alert-container">
                                <Alert
                                    variant={languageAlertType} 
                                    dismissible 
                                    onClose={() => setShowLanguageAlert(false)}
                                    className="mb-0"
                                >
                                    <div className="d-flex align-items-center gap-2">
                                        <FontAwesomeIcon icon={faGlobe} />
                                        <div>
                                            {languageAlertType === 'warning' ? (
                                                <>
                                                    <strong>Translation Required:</strong> You are now editing the {availableLanguages.find(l => l.code === currentLanguage)?.name} version. 
                                                    {currentLanguage !== 'en' && ' Consider copying content from English to start with.'}
                                                </>
                                            ) : (
                                                <>
                                                    <strong>Language Switched:</strong> You are now editing the {availableLanguages.find(l => l.code === currentLanguage)?.name} version of this page.
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </Alert>
                            </Card.Body>
                        )}
                    </Card>
            <Accordion defaultActiveKey="0">
                <Accordion.Item eventKey="0">
                    <Accordion.Header>Logo</Accordion.Header>
                    <Accordion.Body>
                        <div className="mb-3">
                            <Card className="mb-3">
                                <Card.Header className="bg-light">
                                    <h6 className="mb-0">Logo Preview</h6>
                                </Card.Header>
                                <Card.Body>
                                    {logo ? (
                                        <div className="text-center">
                                            <div className="logo-preview-container mb-3 p-3 border rounded bg-white">
                                                <img 
                                                    src={logo} 
                                                    alt="Logo" 
                                                    style={{ 
                                                        maxHeight: '120px',
                                                        maxWidth: '240px',
                                                        
                                                        objectFit: 'contain',
                                                        width: '100%',
                                                        height: 'auto'
                                                    }} 
                                                    className="img-fluid"
                                                />
                                            </div>
                                            <div className="d-flex justify-content-center gap-2 flex-wrap">
                                                <Button 
                                                    variant="outline-primary" 
                                                    size="sm"
                                                    onClick={() => setShowMediaSelector(true)}
                                                >
                                                    <FontAwesomeIcon icon={faEdit} className="me-2" />
                                                    Change
                                                </Button>
                                                <Button 
                                                    variant="outline-danger" 
                                                    size="sm"
                                                    onClick={() => {
                                                        setLogo(null);
                                                        navInstance.setLogo(null);
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} className="me-2" />
                                                    Remove
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center p-4">
                                            <div className="mb-3">
                                                <FontAwesomeIcon 
                                                    icon={faImage} 
                                                    className="text-muted" 
                                                    style={{ fontSize: '3rem' }} 
                                                />
                                            </div>
                                            <p className="text-muted mb-3">No logo selected</p>
                                            <Button 
                                                variant="primary" 
                                                onClick={() => setShowMediaSelector(true)}
                                            >
                                                <FontAwesomeIcon icon={faPlus} className="me-2" />
                                                Select Logo
                                            </Button>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </div>
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="1">
                    <Accordion.Header>Menu Items</Accordion.Header>
                    <Accordion.Body>
                        <Form className="mb-3">
                            <h5>New Item</h5>
                            <Form.Control
                                type="text"
                                placeholder="Enter menu item text"
                                value={newMenuItem}
                                onChange={(e) => setNewMenuItem(e.target.value)}
                            />
                            <Form.Control
                                type="text"
                                placeholder="Enter menu item link"
                                className="mt-2"
                                value={newMenuLink}
                                onChange={(e) => setNewMenuLink(e.target.value)}
                            />
                            <Form.Select 
                                className="mt-2" 
                                value={newItemType} 
                                onChange={(e) => setNewItemType(e.target.value)}
                            >
                                <option value="link">Link</option>
                                <option value="button">Button</option>
                            </Form.Select>
                            {newItemType === 'button' && (
                                <div className="mt-3">
                                    <Form.Label>Button Icon (optional)</Form.Label>
                                    <div className="d-flex flex-wrap gap-2">
                                        {[null, 'hand-holding-heart', 'coffee', 'smile', 'apple-alt', 'anchor', 'bicycle', 'gift'].map((name, idx) => (
                                            <Button
                                                key={idx}
                                                variant={newButtonIcon === name ? 'primary' : 'light'}
                                                className="p-2 d-flex align-items-center"
                                                onClick={(e) => { e.preventDefault(); setNewButtonIcon(name); }}
                                            >
                                                {name ? (
                                                    <>
                                                        <span><i className={`fa-solid fa-${name}`}></i></span>
                                                    </>
                                                ) : (
                                                    <span>None</span>
                                                )}
                                            </Button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className="d-flex justify-content-end mt-2">
                                <Button onClick={addMenuItem}>Add</Button>
                            </div>
                        </Form>

                        <Accordion>
                            {menuItems.sort((a, b) => Number(a.order) - Number(b.order)).map((item, index) => (
                                <Accordion.Item eventKey={item.id || `menu-${index}`} key={item.id || index}>
                                    <Accordion.Header>{item.type === "link" ? item.text : item.button.text}</Accordion.Header>
                                    <Accordion.Body>
                                        {editingItem === (item.type === "link" ? item.id : item.button.id) ? (
                                            <div>
                                                <Form.Control
                                                    type="text"
                                                    value={editedText}
                                                    className="mb-2"
                                                    onChange={(e) => setEditedText(e.target.value)}
                                                    placeholder="Text"
                                                />
                                                <Form.Control
                                                    type="text"
                                                    value={editedLink}
                                                    className="mb-2"
                                                    onChange={(e) => setEditedLink(e.target.value)}
                                                    placeholder="Link"
                                                />
                                                <Form.Control
                                                    type="number"
                                                    value={editedOrder}
                                                    className="mb-2"
                                                    onChange={(e) => setEditedOrder(e.target.value)}
                                                    placeholder="Order"
                                                />
                                                {item.type === 'button' && (
                                                    <div className="mb-2">
                                                        <Form.Label>Button Icon (optional)</Form.Label>
                                                        <div className="d-flex flex-wrap gap-2">
                                                            {[null, 'hand-holding-heart', 'coffee', 'smile', 'apple-alt', 'anchor', 'bicycle', 'gift'].map((name, idx) => (
                                                                <Button
                                                                    key={idx}
                                                                    variant={(item.button.icon ?? null) === name ? 'primary' : 'light'}
                                                                    className="p-2 d-flex align-items-center"
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        const updated = menuItems.map(mi => {
                                                                            if (mi.id === (item.id || item.button.id)) {
                                                                                if (mi.type === 'button') {
                                                                                    return { ...mi, button: { ...mi.button, icon: name } };
                                                                                }
                                                                            }
                                                                            return mi;
                                                                        });
                                                                        setMenuItems(updated);
                                                                        navInstance.setMenuItems(updated);
                                                                    }}
                                                                >
                                                                    {name ? <span><i className={`fa-solid fa-${name}`}></i></span> : <span>None</span>}
                                                                </Button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                <Button className="me-2" variant="success" onClick={() => saveEdit(item.id, item.type)}>Save</Button>
                                                <Button variant="secondary" onClick={() => setEditingItem(null)}>Cancel</Button>
                                            </div>
                                        ) : (
                                            <>
                                                <p><strong>Type:</strong> {item.type}</p>
                                                <p><strong>Link:</strong> {item.type === "link" ? item.link : item.button.link}</p>
                                                {item.type === 'button' && (
                                                    <p><strong>Icon:</strong> {item.button.icon ?? 'None'}</p>
                                                )}
                                                <p><strong>Order:</strong> {item.type === "link" ? item.order || 'Not set' : item.button.order || 'Not set'}</p>
                                                <div className="d-flex justify-content-end">
                                                    <Button variant='warning' onClick={() => startEditing(item)}>Edit</Button>
                                                    <Button className='ms-2' variant='danger' onClick={() => handleRemoveItem(item.id)}>Delete</Button>
                                                </div>
                                            </>
                                        )}
                                    </Accordion.Body>
                                </Accordion.Item>
                            ))}
                        </Accordion>
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="2">
                    <Accordion.Header>Style</Accordion.Header>
                    <Accordion.Body>
                        <div className="mb-3">
                            <label htmlFor="navbarType" className="form-label">Navbar Style</label>
                            <select id="navbarType" className="form-select" onChange={(e) => navInstance.setType(e.target.value)}>
                                <option selected={navInstance.getType() === 'NavBasic'} value="NavBasic">Basic</option>
                                <option selected={navInstance.getType() === 'NavCenter'} value="NavCenter">Centered</option>
                            </select>
                        </div>


                        <div className="mb-3">
                            <label className="form-label">Background Color</label>
                            <div className="d-flex align-items-center">
                                <div 
                                    style={{
                                        width: '30px',
                                        height: '30px',
                                        backgroundColor: currentStyle.NavBasic.backgroundColor,
                                        border: '1px solid #ccc',
                                        marginRight: '10px',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => setShowBgColorPicker(!showBgColorPicker)}
                                />
                                <span>{currentStyle.NavBasic.backgroundColor}</span>
                            </div>
                            {showBgColorPicker && (
                                <div style={{ position: 'absolute', zIndex: 2 }}>
                                    <div 
                                        style={{ position: 'fixed', top: '0', right: '0', bottom: '0', left: '0' }}
                                        onClick={() => setShowBgColorPicker(false)}
                                    />
                                    <SketchPicker 
                                        color={currentStyle.NavBasic.backgroundColor} 
                                        onChange={handleBgColorChange} 
                                    />
                                </div>
                            )}
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Text Color</label>
                            <div className="d-flex align-items-center">
                                <div 
                                    style={{
                                        width: '30px',
                                        height: '30px',
                                        backgroundColor: currentStyle.NavBasicLinkItem.color,
                                        border: '1px solid #ccc',
                                        marginRight: '10px',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => setShowTextColorPicker(!showTextColorPicker)}
                                />
                                <span>{currentStyle.NavBasicLinkItem.color}</span>
                            </div>
                            {showTextColorPicker && (
                                <div style={{ position: 'absolute', zIndex: 2 }}>
                                    <div 
                                        style={{ position: 'fixed', top: '0', right: '0', bottom: '0', left: '0' }}
                                        onClick={() => setShowTextColorPicker(false)}
                                    />
                                    <SketchPicker 
                                        color={currentStyle.NavBasicLinkItem.color} 
                                        onChange={handleTextColorChange} 
                                    />
                                </div>
                            )}
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Text Hover Color</label>
                            <div className="d-flex align-items-center">
                                <div 
                                    style={{
                                        width: '30px',
                                        height: '30px',
                                        backgroundColor: currentStyle.NavBasicLinkItemHover.color,
                                        border: '1px solid #ccc',
                                        marginRight: '10px',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => setShowHoverColorPicker(!showHoverColorPicker)}
                                />
                                <span>{currentStyle.NavBasicLinkItemHover.color}</span>
                            </div>
                            {showHoverColorPicker && (
                                <div style={{ position: 'absolute', zIndex: 2 }}>
                                    <div 
                                        style={{ position: 'fixed', top: '0', right: '0', bottom: '0', left: '0' }}
                                        onClick={() => setShowHoverColorPicker(false)}
                                    />
                                    <SketchPicker 
                                        color={currentStyle.NavBasicLinkItemHover.color} 
                                        onChange={handleHoverColorChange} 
                                    />
                                </div>
                            )}
                        </div>

                        <div className="mb-3">
                            <label htmlFor="navbarPosition" className="form-label">Position</label>
                            <select 
                                id="navbarPosition" 
                                className="form-select" 
                                value={currentStyle.NavBasic.position}
                                onChange={handlePositionChange}
                            >
                                <option value="static">Static</option>
                                <option value="fixed">Fixed</option>
                                <option value="sticky">Sticky</option>
                                <option value="relative">Relative</option>
                                <option value="absolute">Absolute</option>
                            </select>
                        </div>
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="3">
                    <Accordion.Header>
                        {/* <FontAwesomeIcon icon={faCog} className="me-2" /> */}
                        Settings
                    </Accordion.Header>
                    <Accordion.Body>
                        <p className="text-muted small mb-3">
                            Control which utilities appear in the navbar (language switcher and global search).
                        </p>
                        <Form.Group className="mb-3">
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <FontAwesomeIcon icon={faGlobe} className="text-primary" />
                                <Form.Label className="mb-0">Language Switcher</Form.Label>
                            </div>
                            <Form.Check
                                type="switch"
                                id="nav-show-language-switcher"
                                checked={navSettings.showLanguageSwitcher}
                                onChange={(e) => handleSettingsChange('showLanguageSwitcher', e.target.checked)}
                                label={navSettings.showLanguageSwitcher ? 'Visible' : 'Hidden'}
                            />
                        </Form.Group>
                        <Form.Group>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <FontAwesomeIcon icon={faSearch} className="text-primary" />
                                <Form.Label className="mb-0">Search</Form.Label>
                            </div>
                            <Form.Check
                                type="switch"
                                id="nav-show-search"
                                checked={navSettings.showSearch}
                                onChange={(e) => handleSettingsChange('showSearch', e.target.checked)}
                                label={navSettings.showSearch ? 'Visible' : 'Hidden'}
                            />
                        </Form.Group>
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="4">
                    <Accordion.Header>Animation</Accordion.Header>
                    <Accordion.Body>
                        <div className="mb-3">
                            <label htmlFor="animationType" className="form-label">Animation Type</label>
                            <select 
                                id="animationType" 
                                className="form-select" 
                                value={currentAnimation.animationType}
                                onChange={handleAnimationTypeChange}
                            >
                                {availableAnimations.map((animation) => (
                                    <option value={animation}>{animation}</option>
                                ))}
                                
                            </select>
                        </div>

                        <div className="mb-3">
                            <label htmlFor="animationDuration" className="form-label">Duration (ms)</label>
                            <Form.Control 
                                id="animationDuration"
                                type="number"
                                min="0"
                                value={currentAnimation.animationDuration}
                                onChange={handleAnimationDurationChange}
                            />
                        </div>

                        <div className="mb-3">
                            <label htmlFor="animationDelay" className="form-label">Delay (ms)</label>
                            <Form.Control 
                                id="animationDelay"
                                type="number"
                                min="0"
                                value={currentAnimation.animationDelay}
                                onChange={handleAnimationDelayChange}
                            />
                        </div>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>

            <div className="SaveChangesContainer d-flex justify-content-end mt-2">
                <ToastContainer />
                <Button variant='primary' onClick={handleSaveChanges}>Save Changes</Button>
            </div>

            <MediaSelector 
                isOpen={showMediaSelector}
                onClose={() => setShowMediaSelector(false)}
                onSelect={handleLogoSelect}
            />
        </div>
    );
};

export default NavBuilder;